/**
 * Iter 145w — Admin-editable country blocklist for daily slips.
 *
 * Reads + writes `GET /api/admin/country-blocklist`,
 * `PUT /api/admin/country-blocklist`.
 *
 * Drops a federation from every future daily-slip compile (existing
 * cached slips are NOT retroactively scrubbed — the admin should
 * re-run the compile via the cron-manager tile if instant effect is
 * required).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Globe, Plus, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => String(s || "").trim().toLowerCase();

export default function CountryBlocklistManager() {
  const [list, setList] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/country-blocklist`);
      setList(data.current || []);
      setDefaults(data.default || []);
    } catch (e) {
      toast.error("Failed to load country blocklist");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/country-blocklist`, { countries: next });
      setList(data.current || []);
      toast.success(`Blocklist saved · ${data.count} countries`);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const v = norm(input);
    if (!v) return;
    if (list.includes(v)) {
      toast.message("Already in the list");
      setInput("");
      return;
    }
    const next = [...list, v].sort();
    setInput("");
    await persist(next);
  };

  const remove = async (c) => {
    await persist(list.filter((x) => x !== c));
  };

  const restoreDefaults = async () => {
    await persist(defaults);
  };

  return (
    <section className="nd-card p-5 sm:p-6" data-testid="country-blocklist-section">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <Globe className="w-4.5 h-4.5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg sm:text-xl">Country / Federation Blocklist</h2>
            <p className="text-xs text-muted-foreground">
              Fixtures from these countries are excluded from every daily slip slot.
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={restoreDefaults}
          disabled={saving || loading}
          data-testid="country-blocklist-restore-defaults"
          title="Restore the default seed list"
          className="text-xs"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Restore defaults
        </Button>
      </div>

      {/* Input row */}
      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a country e.g. north-korea"
          className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/30"
          data-testid="country-blocklist-input"
          disabled={saving || loading}
        />
        <Button
          onClick={add}
          disabled={!input.trim() || saving || loading}
          data-testid="country-blocklist-add-btn"
          className="h-9 px-4"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Pills */}
      <div className="flex flex-wrap gap-2" data-testid="country-blocklist-pills">
        {loading ? (
          <span className="text-xs text-muted-foreground">Loading…</span>
        ) : list.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">
            No countries blocked — every federation is allowed in daily slips.
          </span>
        ) : (
          list.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1.5 bg-foreground/5 border border-border rounded-full pl-3 pr-1 py-1 text-xs font-medium capitalize"
              data-testid={`country-blocklist-pill-${c}`}
            >
              {c}
              <button
                type="button"
                onClick={() => remove(c)}
                disabled={saving}
                className="h-5 w-5 rounded-full hover:bg-foreground/10 flex items-center justify-center"
                title={`Remove ${c}`}
                data-testid={`country-blocklist-remove-${c}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">
        Changes take effect on the next daily-slip compile (08:00 / 16:30 / 19:00 UTC).
        Existing cached slips are NOT retroactively scrubbed.
      </p>
    </section>
  );
}
